<template>
  <v-dialog max-width="1200px" v-model="showDialog" persistent scrollable>
    <v-card class="bulk-update-dialog">
      <v-card-title class="dialog-head">
        <div>
          <div class="dialog-title">
            {{ step === "reason" ? "Bulk Update" : `Bulk Update — ${reasonLabel}` }}
          </div>
          <div class="dialog-subtitle">
            <template v-if="step === 'reason'">Choose the reason you want to fix across several Draft DAs at once.</template>
            <template v-else-if="step === 'rows'">These Open Draft DAs match this reason. Select the rows to include, page by page.</template>
            <template v-else>Here's what happened when the fix was applied.</template>
          </div>
        </div>
        <v-btn icon small @click="closeDialog" class="dialog-close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Step 1: pick a reason -->
      <v-card-text v-if="step === 'reason'" class="px-6 pt-5 pb-3">
        <v-skeleton-loader v-if="reasonsLoading" type="list-item@4" />
        <div v-else-if="!reasons.length" class="body-text grey--text text-center py-8">
          No Open Draft DAs to update right now.
        </div>
        <template v-else>
          <div class="section-label mb-2">Failure Reason</div>
          <v-card outlined class="reason-menu">
            <div
              v-for="r in reasons"
              :key="r.key"
              @click="selectReason(r)"
              class="reason-item"
            >
              <div class="reason-icon" :class="isContractReason(r.key) ? 'reason-icon--danger' : 'reason-icon--accent'">
                <v-icon :color="isContractReason(r.key) ? 'error' : 'primary'" size="20">
                  {{ reasonIcon(r.key) }}
                </v-icon>
              </div>
              <span class="reason-title">{{ r.label }}</span>
              <v-spacer />
              <span class="reason-count">{{ r.count }} row{{ r.count === 1 ? "" : "s" }}</span>
            </div>
          </v-card>
        </template>
      </v-card-text>

      <!-- Step 2: matching rows (paginated grid) + fix -->
      <v-card-text v-else-if="step === 'rows'" class="px-6 pt-5 pb-3">
        <v-alert v-if="isContractReason(reasonKey)" dense text type="error" class="body-text mb-3">
          Nothing to pick here — add the missing
          {{ reasonKey === "vendor_rate" ? "sub-hire rate" : "contract/rate" }} in
          <router-link v-if="reasonKey === 'vendor_rate'" to="/app/admin/transporter-contract/list">Vendor Tariff</router-link>
          <router-link v-else to="/app/admin/customer-contract/list">Customer Tariff</router-link>,
          then hit Retry. It re-checks each selected row before trying to create its DA again.
        </v-alert>

        <div class="d-flex mb-3" style="gap:12px">
          <DateField
            :inputProps="{
              dense: true,
              outlined: true,
              label: 'Movement Date From',
              'hide-details': 'auto',
              clearable: true,
            }"
            v-model="movementDateFrom"
            @change="onMovementDateFilterChange"
          />
          <DateField
            :inputProps="{
              dense: true,
              outlined: true,
              label: 'Movement Date To',
              'hide-details': 'auto',
              clearable: true,
            }"
            v-model="movementDateTo"
            @change="onMovementDateFilterChange"
          />
        </div>

        <v-card outlined class="mb-3">
          <div class="d-flex align-center px-4 py-2 grey lighten-4">
            <span class="font-weight-bold text-body-1">
              {{ totalRowCount }} matching row{{ totalRowCount === 1 ? "" : "s" }}
              <span class="font-weight-regular grey--text">— {{ selectedRowIds.length }} selected</span>
            </span>
            <v-spacer />
            <Pagination
              v-if="totalRowCount > 0"
              :pageNo="pageNo"
              :totalItems="totalRowCount"
              :pageSize="itemsPerPage"
              @itemsPerPageChange="onItemsPerPageChange"
              @prevPage="prevPage"
              @nextPage="nextPage"
            />
          </div>
          <v-data-table
            :value="selectedRowsOnPage"
            @input="onSelectionChanged"
            :headers="rowHeaders"
            :items="rows"
            item-key="id"
            show-select
            hide-default-footer
            disable-pagination
            disable-sort
            :loading="rowsLoading"
            :items-per-page="itemsPerPage"
            :checkbox-color="'primary'"
          >
            <template v-slot:[`item.data-table-select`]="{ item, isSelected, select }">
              <v-tooltip v-if="!isRowSelectable(item)" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <v-simple-checkbox :value="false" disabled />
                  </span>
                </template>
                <span>Nothing resolved yet for this row - fix it in Customer/Vendor Tariff, then Retry.</span>
              </v-tooltip>
              <v-simple-checkbox v-else :value="isSelected" @input="select($event)" />
            </template>
            <template v-slot:[`item.failure_reason`]="{ item }">
              <span class="text-body-2" style="display:block; max-width:280px; white-space:normal">
                {{ item.failure_reason }}
              </span>
            </template>
            <template v-slot:[`item.resolution`]="{ item }">
              <span v-if="isContractReason(reasonKey)" :class="item.resolved_label ? 'success--text font-weight-medium' : 'grey--text'">
                {{ item.resolved_label || "Needs fallback" }}
              </span>
              <template v-else>
                <span v-if="item.resolved_label" class="success--text font-weight-medium text-body-2">
                  {{ item.resolved_label }}
                </span>
                <Autocomplete
                  v-else-if="reasonKey === 'driver'"
                  :items="driverList"
                  itemText="display_name"
                  itemValue="id"
                  :loading="driverLoading"
                  :totalItems="driverCount"
                  @updateList="fetchDrivers"
                  hide-details="auto"
                  placeholder="Select a driver"
                  style="min-width:220px"
                  :value="rowFallbackValues[item.id]"
                  @input="setRowFallbackValue(item.id, $event)"
                />
                <Autocomplete
                  v-else-if="reasonKey === 'vehicle'"
                  :items="vehicleList"
                  itemText="vehicle_number"
                  itemValue="id"
                  :loading="vehicleLoading"
                  :totalItems="vehicleCount"
                  @updateList="fetchVehicles"
                  hide-details="auto"
                  placeholder="Select a vehicle"
                  style="min-width:220px"
                  :value="rowFallbackValues[item.id]"
                  @input="setRowFallbackValue(item.id, $event)"
                />
                <MultiSelect
                  v-else-if="reasonKey === 'vendor'"
                  :items="vendorList"
                  itemText="company_title"
                  itemValue="id"
                  outlined
                  dense
                  clearable
                  hide-details="auto"
                  placeholder="Select a vendor"
                  style="min-width:220px"
                  :value="rowFallbackValues[item.id]"
                  @input="setRowFallbackValue(item.id, $event)"
                />
                <span v-else class="grey--text text-body-2">Needs fallback</span>
              </template>
            </template>
            <template v-slot:no-data>
              <div class="py-6 text-center grey--text">No matching Draft DAs.</div>
            </template>
          </v-data-table>
        </v-card>
      </v-card-text>

      <!-- Step 3: result -->
      <v-card-text v-else class="px-6 pt-5 pb-3">
        <v-alert dense text :type="resultFailedCount ? 'warning' : 'success'" class="body-text mb-3">
          {{ resultConfirmedCount }} confirmed as real DAs.
          <template v-if="resultFailedCount"> {{ resultFailedCount }} still need attention — left Open.</template>
        </v-alert>
        <div style="max-height: 360px; overflow-y: auto">
          <div v-for="r in results" :key="r.id" class="d-flex align-start px-2 py-3 row-item">
            <v-icon :color="r.result === 'confirmed' ? 'green' : 'grey'" class="mr-3 mt-1">
              {{ r.result === "confirmed" ? "mdi-check-circle" : "mdi-alert-circle-outline" }}
            </v-icon>
            <div class="flex-grow-1" style="min-width:0">
              <div class="font-weight-bold text-body-1">
                {{ rowDataById[r.id] ? rowDataById[r.id].dda_no : r.id }}
              </div>
              <div class="text-body-2 grey--text text--darken-1 mt-1" style="line-height:1.5; word-break:break-word">
                {{ r.result === "confirmed" ? `DA ${r.da_no}` : (r.detail || "Still needs attention") }}
              </div>
            </div>
          </div>
        </div>
      </v-card-text>

      <v-divider class="mt-1" />
      <v-card-actions class="px-6 py-4">
        <span class="body-text grey--text">{{ footHint }}</span>
        <v-spacer />
        <v-btn v-if="step === 'rows'" depressed outlined @click="step = 'reason'" class="mr-2">Back</v-btn>
        <v-btn v-if="step !== 'result'" depressed outlined @click="closeDialog" class="mr-2">Cancel</v-btn>
        <v-btn
          v-if="step === 'rows'"
          color="primary"
          depressed
          :loading="applying"
          :disabled="!canApplyUpdate"
          min-width="140"
          @click="applyUpdate"
        >
          <v-icon small class="mr-1">{{ isContractReason(reasonKey) ? "mdi-refresh" : "mdi-check-circle" }}</v-icon>
          {{ isContractReason(reasonKey) ? "Retry" : "Apply & Retry" }} ({{ selectedRowIds.length }})
        </v-btn>
        <v-btn v-if="step === 'result'" color="primary" depressed @click="closeDialog">Done</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Autocomplete from "@/components/FormBaseComponents/Autocomplete.vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";
import Pagination from "@/components/General/Pagination.vue";
import DateField from "@/components/FormBaseComponents/DateField.vue";

const REASON_ICONS = {
  driver: "mdi-account-outline",
  vehicle: "mdi-truck-outline",
  vendor: "mdi-domain",
  customer_contract: "mdi-file-document-alert-outline",
  vendor_rate: "mdi-currency-usd-off",
  other: "mdi-help-circle-outline",
};
const CONTRACT_REASONS = ["customer_contract", "vendor_rate"];

export default {
  name: "DraftDABulkUpdateDialog",
  components: { Autocomplete, MultiSelect, Pagination, DateField },
  props: {
    value: { type: Boolean, default: false },
  },
  watch: {
    value(val) {
      if (val) {
        this.resetToReasonStep();
        this.fetchReasons();
      }
    },
  },
  data() {
    return {
      step: "reason",
      movementDateFrom: null,
      movementDateTo: null,
      reasonsLoading: false,
      reasons: [],
      reasonKey: null,
      reasonLabel: "",
      rowsLoading: false,
      rows: [],
      totalRowCount: 0,
      pageNo: 1,
      itemsPerPage: 50,
      // selectedRowIds is the cross-page selection, merged by hand on page change
      selectedRowIds: [],
      rowDataById: {},
      rowFallbackValues: {},
      driverList: [],
      driverCount: 0,
      driverLoading: false,
      vehicleList: [],
      vehicleCount: 0,
      vehicleLoading: false,
      vendorList: [],
      applying: false,
      results: [],
    };
  },
  computed: {
    showDialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    rowHeaders() {
      const columns = [
        { text: "Draft DA No", value: "dda_no", width: 140 },
        { text: "Container No", value: "container_number", width: 150 },
        { text: "Failure Reason", value: "failure_reason" },
        {
          text: this.isContractReason(this.reasonKey) ? "Status" : "Resolution / Fallback",
          value: "resolution",
          width: this.isContractReason(this.reasonKey) ? 220 : 300,
        },
      ];
      return columns;
    },
    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
    selectedRowsOnPage() {
      return this.rows.filter((row) => this.selectedRowIds.includes(row.id));
    },
    canApplyUpdate() {
      if (!this.selectedRowIds.length) return false;
      if (this.isContractReason(this.reasonKey)) return true;
      return this.selectedRowIds.every((id) => {
        const row = this.rowDataById[id];
        return row && (row.resolved_label || this.getVal(this.rowFallbackValues[id]));
      });
    },
    resultConfirmedCount() {
      return this.results.filter((r) => r.result === "confirmed").length;
    },
    resultFailedCount() {
      return this.results.length - this.resultConfirmedCount;
    },
    footHint() {
      if (this.step === "reason") return "Select a reason to see its matching Draft DAs";
      if (this.step === "rows") {
        if (this.isContractReason(this.reasonKey)) {
          return "Re-checks the contract/rate for each selected row and creates the DA if one now exists";
        }
        if (this.selectedRowIds.length && !this.canApplyUpdate) {
          return "Some selected rows still show \"Needs fallback\" - pick a value and Apply to include them";
        }
        return "Confirms auto-resolved rows as-is; applies the picked fallback to the rest";
      }
      return "";
    },
  },
  methods: {
    getVal(v) {
      return v && typeof v === "object" ? v.id : v;
    },
    reasonIcon(key) {
      return REASON_ICONS[key] || REASON_ICONS.other;
    },
    isContractReason(key) {
      return CONTRACT_REASONS.includes(key);
    },
    isRowSelectable(item) {
      if (!item) return false;
      if (this.isContractReason(this.reasonKey)) return !!item.resolved_label;
      return true;
    },
    resetToReasonStep() {
      this.step = "reason";
      this.movementDateFrom = null;
      this.movementDateTo = null;
      this.reasonKey = null;
      this.reasonLabel = "";
      this.rows = [];
      this.totalRowCount = 0;
      this.pageNo = 1;
      this.selectedRowIds = [];
      this.rowDataById = {};
      this.rowFallbackValues = {};
      this.results = [];
    },
    closeDialog() {
      this.showDialog = false;
      this.$emit("updated");
    },
    fetchReasons() {
      this.reasonsLoading = true;
      this.$api.draftDA
        .getBulkUpdateReasons()
        .then((res) => {
          const data = res.data || res;
          this.reasons = data.reasons || [];
        })
        .catch((err) => {
          this.$bus.$emit("showToastMessage", {
            message: err?.message || "Failed to load Draft DA reasons.",
            color: "error",
          });
        })
        .finally(() => {
          this.reasonsLoading = false;
        });
    },
    selectReason(reason) {
      this.reasonKey = reason.key;
      this.reasonLabel = reason.label;
      this.pageNo = 1;
      this.selectedRowIds = [];
      this.rowDataById = {};
      this.rowFallbackValues = {};
      this.step = "rows";
      this.fetchRows();
      if (reason.key === "driver") this.fetchDrivers({ limit: 10, offset: 0 });
      if (reason.key === "vehicle") this.fetchVehicles({ limit: 10, offset: 0 });
      if (reason.key === "vendor") this.fetchVendors();
    },
    onSelectionChanged(selectedOnPage) {
      const selectedIdsOnPage = selectedOnPage.map((r) => r.id);
      const pageIds = this.rows.map((r) => r.id);
      const keptFromOtherPages = this.selectedRowIds.filter((id) => !pageIds.includes(id));
      this.selectedRowIds = [...keptFromOtherPages, ...selectedIdsOnPage];
    },
    setRowFallbackValue(id, value) {
      this.rowFallbackValues = { ...this.rowFallbackValues, [id]: value };
    },
    fetchRows() {
      this.rowsLoading = true;
      const params = { offset: this.offset, limit: this.itemsPerPage };
      if (this.movementDateFrom) params.movement_date_from = this.movementDateFrom;
      if (this.movementDateTo) params.movement_date_to = this.movementDateTo;
      this.$api.draftDA
        .getBulkUpdateRows(this.reasonKey, params)
        .then((res) => {
          const data = res.data || res;
          this.rows = data.rows || [];
          this.totalRowCount = data.count != null ? data.count : this.rows.length;
          const cached = { ...this.rowDataById };
          this.rows.forEach((row) => {
            cached[row.id] = row;
          });
          this.rowDataById = cached;
        })
        .catch((err) => {
          this.$bus.$emit("showToastMessage", {
            message: err?.message || "Failed to load matching Draft DAs.",
            color: "error",
          });
        })
        .finally(() => {
          this.rowsLoading = false;
        });
    },
    onItemsPerPageChange(size) {
      this.pageNo = 1;
      this.itemsPerPage = size;
      this.fetchRows();
    },
    prevPage() {
      this.pageNo--;
      this.fetchRows();
    },
    nextPage() {
      this.pageNo++;
      this.fetchRows();
    },
    onMovementDateFilterChange() {
      this.pageNo = 1;
      this.selectedRowIds = [];
      this.fetchRows();
    },
    fetchDrivers(params = {}) {
      this.driverLoading = true;
      this.$api.driver
        .getDriverSelectList({ limit: 10, ...params })
        .then((res) => {
          const drivers = res.data.map((driver) => ({
            ...driver,
            display_name: driver.license_number ? `${driver.driver_name} (${driver.license_number})` : driver.driver_name,
          }));
          this.driverList = params.offset ? [...this.driverList, ...drivers] : drivers;
          this.driverCount = res.count;
        })
        .finally(() => {
          this.driverLoading = false;
        });
    },
    fetchVehicles(params = {}) {
      this.vehicleLoading = true;
      this.$api.vehicle
        .getVehicleSelectList({ limit: 10, ...params })
        .then((res) => {
          this.vehicleList = params.offset ? [...this.vehicleList, ...res.data] : res.data;
          this.vehicleCount = res.count;
        })
        .finally(() => {
          this.vehicleLoading = false;
        });
    },
    fetchVendors() {
      this.$api.company
        .getCompanySelectList({ company_type: "Transporter", limit: 500 })
        .then((res) => {
          this.vendorList = res.data;
        })
        .catch(() => {});
    },
    applyUpdate() {
      this.applying = true;
      const payload = {
        reason: this.reasonKey,
        rows: this.selectedRowIds.map((id) => ({
          id,
          fallback_id: this.getVal(this.rowFallbackValues[id]),
        })),
      };
      this.$api.draftDA
        .bulkUpdateDraftDA(payload)
        .then((res) => {
          const data = res.data || res;
          this.results = data.results || [];
          this.step = "result";
        })
        .catch((err) => {
          this.$bus.$emit("showToastMessage", {
            message: err?.message || "Bulk update failed.",
            color: "error",
          });
        })
        .finally(() => {
          this.applying = false;
        });
    },
  },
};
</script>

<style scoped>
.bulk-update-dialog ::v-deep .v-card__text,
.bulk-update-dialog ::v-deep .v-card__actions {
  font-size: 14px;
}

.dialog-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.dialog-title {
  font-size: 19px;
  font-weight: 700;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.87);
}
.dialog-subtitle {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 4px;
  line-height: 1.4;
}
.dialog-close {
  flex: none;
  margin-top: 2px;
}

.section-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.5);
}
.section-sublabel {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.55);
  margin-top: -4px;
}
.body-text {
  font-size: 14px !important;
  line-height: 1.5;
}

.reason-menu {
  border-radius: 10px;
  overflow: hidden;
}
.reason-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 16px;
  cursor: pointer;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.reason-item:last-child {
  border-bottom: none;
}
.reason-item:hover {
  background: #fafafa;
}
.reason-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}
.reason-icon--accent {
  background: rgba(240, 112, 37, 0.12);
}
.reason-icon--danger {
  background: rgba(194, 59, 59, 0.1);
}
.reason-title {
  font-size: 15px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
}
.reason-count {
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.45);
  flex: none;
}

.row-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.row-item:last-child {
  border-bottom: none;
}
</style>
